import type { MutableRefObject } from 'react';
import type { EntityTableAdapter } from './EntityTableAdapter';
import type { TableColumn } from '../GenericTable';
import type { ScimGroup } from '../../../types/Groups';
import type { ScimEntityService } from '../../../services/ScimEntityService';
import { extractListLevelDollarUnderscoreProps } from '../../../utils/listResponseMeta';

export function createGroupsManagementTableAdapter(args: {
    authResolved: boolean;
    entityServiceRef: MutableRefObject<ScimEntityService | undefined>;
    activeFilterTabRef: MutableRefObject<number>;
    allowedTableAttributeIdsRef: MutableRefObject<string[] | null | undefined>;
    itemsPerPage: number;
    getColumns: () => TableColumn<ScimGroup>[];
}): EntityTableAdapter<ScimGroup> {
    const {
        authResolved,
        entityServiceRef,
        activeFilterTabRef,
        allowedTableAttributeIdsRef,
        itemsPerPage,
        getColumns
    } = args;

    return {
        entityType: 'group',
        getColumns,
        loadData: async (
            page,
            abortSignal,
            searchFilter,
            _selectedType,
            sortBy,
            searchtext,
            _searchAttributes,
            omitSearchDialectExpression
        ) => {
            const svc = entityServiceRef.current;
            if (!authResolved || !svc) {
                return { data: [], totalResults: 0, currentPage: 1, totalPages: 1, hasNextPage: false, hasPreviousPage: false };
            }
            const filterTab = activeFilterTabRef.current;
            let tableAttributes =
                filterTab === 1
                    ? ['Id', 'DisplayName', 'Type', 'ManagedType', 'LegacyFilter', 'FilterScope', 'Filter']
                    : ['Id', 'DisplayName', 'Description', 'Type', 'ManagedType', 'Scope', 'Email'];
            const allowed = allowedTableAttributeIdsRef.current;
            if (Array.isArray(allowed)) {
                const alwaysRequest = ['Id', 'DisplayName'];
                tableAttributes = [...new Set([...alwaysRequest, ...allowed])];
            }
            let effectiveFilter = searchFilter;
            if (filterTab === 1 && !effectiveFilter) {
                effectiveFilter = 'LegacyFilter pr';
            } else if (filterTab === 1 && effectiveFilter) {
                effectiveFilter = `(${effectiveFilter}) and LegacyFilter pr`;
            }
            let response;
            if (effectiveFilter) {
                response = await svc.searchGroups({
                    filter: effectiveFilter,
                    page,
                    pageSize: itemsPerPage,
                    sortBy,
                    attributes: tableAttributes,
                    abortSignal
                });
            } else if (searchtext) {
                response = await svc.searchGroups({
                    page,
                    pageSize: itemsPerPage,
                    sortBy,
                    attributes: tableAttributes,
                    searchtext,
                    omitSearchDialectExpression,
                    abortSignal
                });
            } else {
                response = await svc.getGroups(page, itemsPerPage, sortBy, tableAttributes, abortSignal);
            }
            const groups = response.groups || [];
            const total = response.totalResults || 0;
            const totalPages = Math.ceil(total / itemsPerPage);
            const listLevelMeta = extractListLevelDollarUnderscoreProps(response);
            return {
                ...listLevelMeta,
                data: groups,
                totalResults: total,
                currentPage: page,
                totalPages: totalPages || 1,
                hasNextPage: page < totalPages,
                hasPreviousPage: page > 1
            };
        },
        deleteById: async (id: string) => {
            const svc = entityServiceRef.current;
            if (!svc) return;
            await svc.deleteEntity('Group', id);
        }
    };
}
